/** Home — pick up where you left off, background-task health, quick links.
 *  Recents come from the ⌘K palette's localStorage store (no backend);
 *  tasks reuse /bgtasks/ with a light 30 s poll while the page is open. */
import HistoryIcon from '@mui/icons-material/History';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import {
  Box,
  Button,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import * as bgtasksApi from '@/api/bgtasks';
import { TASK_STATE } from '@/api/bgtasks';
import { useAuth } from '@/auth/AuthProvider';
import { readRecents } from '@/components/CommandPalette/recents';
import { usePermissions } from '@/hooks/usePermissions';
import { formatRelativeTime } from '@/utils/format';

type ChipColor = 'default' | 'info' | 'success' | 'error' | 'warning';

const STATE_COLOR: Record<number, ChipColor> = {
  [TASK_STATE.PENDING]: 'default',
  [TASK_STATE.RUNNING]: 'info',
  [TASK_STATE.COMPLETED]: 'success',
  [TASK_STATE.FAILED]: 'error',
  [TASK_STATE.CANCELLED]: 'default',
  [TASK_STATE.AWAITING_APPROVAL]: 'warning',
};

const QUICK_LINKS: { label: string; to: string; perm?: string }[] = [
  { label: 'Bookings', to: '/bookings', perm: 'console_bookings_view' },
  { label: 'Tour Operators', to: '/tour-operators', perm: 'console_top_view' },
  { label: 'Users', to: '/users/system', perm: 'console_user_view' },
  { label: 'Background Tasks', to: '/bgtasks' },
  { label: 'Audit Log', to: '/audit', perm: 'console_audit_view' },
];

function Card({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, p: 2 }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
          {title}
        </Typography>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

export function DashboardPage() {
  const auth = useAuth();
  const { hasPerm } = usePermissions();
  const navigate = useNavigate();
  const recents = useMemo(() => readRecents().slice(0, 8), []);

  const tasks = useQuery({
    queryKey: ['dashboard', 'bgtasks'],
    queryFn: () => bgtasksApi.list({ page_size: 5 }),
    refetchInterval: 30_000,
  });

  const quickLinks = QUICK_LINKS.filter((l) => !l.perm || hasPerm(l.perm));

  return (
    <Box sx={{ maxWidth: 1000 }}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>
        Welcome, {auth.user?.first_name || auth.user?.email}.
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Jump anywhere with <Chip size="small" label="⌘K" component="span" /> — pages, records,
        users, or an ID.
      </Typography>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
        <Card
          title="Pick up where you left off"
          action={<HistoryIcon fontSize="small" color="disabled" />}
        >
          {recents.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              Records you open will appear here.
            </Typography>
          ) : (
            <List dense disablePadding>
              {recents.map((r) => (
                <ListItemButton key={r.to} component={RouterLink} to={r.to} sx={{ borderRadius: 1 }}>
                  <ListItemText primary={r.label} secondary={r.area} />
                </ListItemButton>
              ))}
            </List>
          )}
        </Card>

        <Card
          title="Background tasks"
          action={
            <Button size="small" component={RouterLink} to="/bgtasks" startIcon={<TaskAltIcon />}>
              View all
            </Button>
          }
        >
          {tasks.isError ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              Couldn’t load tasks.
            </Typography>
          ) : (tasks.data?.results ?? []).length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              {tasks.isLoading ? 'Loading…' : 'No background tasks yet.'}
            </Typography>
          ) : (
            <List dense disablePadding>
              {(tasks.data?.results ?? []).map((t) => (
                <ListItemButton key={t.id} onClick={() => navigate(`/bgtasks/${t.id}`)} sx={{ borderRadius: 1 }}>
                  <ListItemText
                    primary={t.task_label}
                    secondary={[t.user_email, formatRelativeTime(t.created_ts)].filter(Boolean).join(' · ')}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    color={STATE_COLOR[t.state] ?? 'default'}
                    label={t.state_display}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Card>
      </Stack>

      {quickLinks.length > 0 && (
        <Stack direction="row" spacing={1} sx={{ mt: 3, flexWrap: 'wrap', rowGap: 1 }}>
          {quickLinks.map((l) => (
            <Button key={l.to} size="small" variant="outlined" component={RouterLink} to={l.to}>
              {l.label}
            </Button>
          ))}
        </Stack>
      )}
    </Box>
  );
}
